import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Trophy,
  Users,
  Calendar,
  MapPin,
  BarChart2,
  Megaphone,
  ArrowRight,
  Table,
  Radio,
  Sparkles,
  ShieldCheck,
  Clock3,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { API } from '../lib/api';
import { cn } from '../lib/utils';

const TournamentDetail = () => {
  const { tournamentId } = useParams();
  const { getAuthHeader, isOrganizer } = useAuth();
  const { isDark } = useTheme();
  const [tournament, setTournament] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (showError = true) => {
    try {
      const [tournamentRes, teamsRes, matchesRes] = await Promise.all([
        axios.get(`${API}/tournaments/${tournamentId}`, getAuthHeader()),
        axios.get(`${API}/tournaments/${tournamentId}/teams`, getAuthHeader()),
        axios.get(`${API}/tournaments/${tournamentId}/matches`, getAuthHeader()),
      ]);
      setTournament(tournamentRes.data);
      setTeams(teamsRes.data);
      setMatches(matchesRes.data);
    } catch (error) {
      if (showError) toast.error('Failed to load tournament');
    } finally {
      setLoading(false);
    }
  }, [tournamentId, getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useAutoRefresh(() => fetchData(false));

  const getStatusTone = (status) => {
    switch (status) {
      case 'live':
        return 'bg-red-500 text-white';
      case 'completed':
        return 'bg-emerald-600 text-white';
      case 'upcoming':
      case 'scheduled':
        return 'bg-sky-600 text-white';
      default:
        return isDark ? 'bg-white/10 text-white/70' : 'bg-slate-900/8 text-slate-700';
    }
  };

  const surfaceCardClass = isDark ? 'border-white/10 bg-white/[0.04]' : 'border-slate-900/10 bg-white/82';
  const innerCardClass = isDark ? 'border-white/10 bg-black/20' : 'border-slate-900/10 bg-slate-100/85';
  const headingClass = isDark ? 'text-white' : 'text-slate-950';
  const mutedTextClass = isDark ? 'text-white/55' : 'text-slate-600';
  const subtleTextClass = isDark ? 'text-white/45' : 'text-slate-500';
  const eyebrowClass = isDark ? 'text-white/35' : 'text-slate-500';

  if (loading) {
    return (
      <Layout title="Tournament">
        <div className="space-y-6">
          <div className={cn('h-56 animate-pulse rounded-[28px] border', surfaceCardClass)} />
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className={cn('h-32 animate-pulse rounded-3xl border', surfaceCardClass)} />
            ))}
          </div>
          <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
            <div className={cn('h-[420px] animate-pulse rounded-[28px] border', surfaceCardClass)} />
            <div className={cn('h-[420px] animate-pulse rounded-[28px] border', surfaceCardClass)} />
          </div>
        </div>
      </Layout>
    );
  }

  if (!tournament) {
    return (
      <Layout title="Tournament">
        <Card className={cn('rounded-[28px] border-dashed', surfaceCardClass)}>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Trophy className={cn('mb-4 h-16 w-16', isDark ? 'text-white/20' : 'text-slate-300')} />
            <h2 className={cn('text-xl font-semibold', headingClass)}>Tournament not found</h2>
            <p className={cn('mt-2', subtleTextClass)}>This tournament hub could not be loaded.</p>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  const liveMatches = matches.filter((match) => match.status === 'live');
  const upcomingMatches = matches.filter((match) => match.status === 'scheduled').slice(0, 4);
  const completedMatches = matches.filter((match) => match.status === 'completed');

  const sections = [
    {
      path: `/tournaments/${tournamentId}/teams`,
      label: 'Teams & Players',
      icon: Users,
      description: `${teams.length} squads registered`,
      tone: 'text-sky-300',
      bg: 'bg-sky-500/10',
    },
    {
      path: `/tournaments/${tournamentId}/matches`,
      label: 'Match Schedule',
      icon: Calendar,
      description: `${matches.length} fixtures mapped`,
      tone: 'text-violet-300',
      bg: 'bg-violet-500/10',
    },
    {
      path: `/tournaments/${tournamentId}/points`,
      label: 'Standings',
      icon: Table,
      description: 'Dynamic league table',
      tone: 'text-emerald-300',
      bg: 'bg-emerald-500/10',
    },
    {
      path: `/tournaments/${tournamentId}/stats`,
      label: 'Player Statistics',
      icon: BarChart2,
      description: 'Top performers and trends',
      tone: 'text-amber-300',
      bg: 'bg-amber-500/10',
    },
    {
      path: `/tournaments/${tournamentId}/announcements`,
      label: 'Announcements',
      icon: Megaphone,
      description: 'Official updates and notices',
      tone: 'text-pink-300',
      bg: 'bg-pink-500/10',
    },
  ];

  return (
    <Layout title="Tournament">
      <div className="space-y-6">
        <Card
          className={cn(
            'overflow-hidden rounded-[30px] border-primary/20',
            isDark
              ? 'bg-[linear-gradient(135deg,rgba(170,255,0,0.15),rgba(255,255,255,0.03)_55%,rgba(255,255,255,0.01))]'
              : 'bg-[linear-gradient(135deg,rgba(170,255,0,0.16),rgba(255,255,255,0.92)_58%,rgba(240,244,237,0.86))]'
          )}
        >
          <CardContent className="p-6 sm:p-8">
            <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={cn('rounded-full capitalize', getStatusTone(tournament.status))}>
                    {tournament.status}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={cn(
                      'rounded-full capitalize',
                      isDark ? 'border-white/10 bg-white/[0.04] text-white/75' : 'border-slate-900/10 bg-white/80 text-slate-700'
                    )}
                  >
                    {tournament.format}
                  </Badge>
                  {isOrganizer() && (
                    <Badge className="rounded-full border border-primary/20 bg-primary/10 text-primary">
                      <ShieldCheck className="mr-1 h-3 w-3" />
                      Organizer View
                    </Badge>
                  )}
                </div>

                <h1 className={cn('mt-4 text-3xl font-bold tracking-tight sm:text-4xl', headingClass)}>{tournament.name}</h1>
                <p className={cn('mt-3 max-w-2xl text-sm leading-6', isDark ? 'text-white/65' : 'text-slate-600')}>
                  This tournament hub brings fixtures, squads, standings, live scoring, and official updates into one responsive control surface.
                </p>

                <div className={cn('mt-6 flex flex-wrap gap-4 text-sm', isDark ? 'text-white/58' : 'text-slate-700')}>
                  <span className="inline-flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    {new Date(tournament.start_date).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                  {tournament.venue && (
                    <span className="inline-flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      {tournament.venue}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-primary" />
                    {tournament.overs_per_match} overs
                  </span>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to={`/tournaments/${tournamentId}/matches`}>
                    <Button className="rounded-full px-5">
                      Open Match Center
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to={`/tournaments/${tournamentId}/teams`}>
                    <Button
                      variant="outline"
                      className={cn(
                        'rounded-full',
                        isDark
                          ? 'border-white/10 bg-black/20 text-white hover:bg-white/[0.06]'
                          : 'border-slate-900/10 bg-white/80 text-slate-900 hover:bg-white'
                      )}
                    >
                      View Teams
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-2">
                <Card className={cn('rounded-3xl', innerCardClass)}>
                  <CardContent className="p-4 text-center">
                    <p className={cn('text-[11px] uppercase tracking-[0.24em]', eyebrowClass)}>Teams</p>
                    <p className={cn('mt-2 text-3xl font-bold score-display', headingClass)}>{teams.length}</p>
                  </CardContent>
                </Card>
                <Card className={cn('rounded-3xl', innerCardClass)}>
                  <CardContent className="p-4 text-center">
                    <p className={cn('text-[11px] uppercase tracking-[0.24em]', eyebrowClass)}>Fixtures</p>
                    <p className={cn('mt-2 text-3xl font-bold score-display', headingClass)}>{matches.length}</p>
                  </CardContent>
                </Card>
                <Card className={cn('rounded-3xl', innerCardClass)}>
                  <CardContent className="p-4 text-center">
                    <p className={cn('text-[11px] uppercase tracking-[0.24em]', eyebrowClass)}>Live</p>
                    <p className="mt-2 text-3xl font-bold text-red-300 score-display">{liveMatches.length}</p>
                  </CardContent>
                </Card>
                <Card className={cn('rounded-3xl', innerCardClass)}>
                  <CardContent className="p-4 text-center">
                    <p className={cn('text-[11px] uppercase tracking-[0.24em]', eyebrowClass)}>Completed</p>
                    <p className={cn('mt-2 text-3xl font-bold score-display', headingClass)}>{completedMatches.length}</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </CardContent>
        </Card>

        {liveMatches.length > 0 && (
          <Card className="rounded-[28px] border-red-500/20 bg-red-500/[0.07]" data-testid="live-matches-alert">
            <CardHeader className="pb-3">
              <CardTitle className={cn('flex items-center gap-2 text-lg', headingClass)}>
                <Radio className="h-5 w-5 text-red-300 pulse-live" />
                Live Match Center
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {liveMatches.map((match) => {
                const liveMatchPath = isOrganizer() ? `/scoring/${match.id}` : `/live/${match.id}`;
                const innings = match.innings?.[match.innings.length - 1];

                return (
                  <Link
                    key={match.id}
                    to={liveMatchPath}
                    className={cn(
                      'block rounded-3xl border p-4 transition-colors',
                      isDark
                        ? 'border-white/10 bg-black/20 hover:bg-white/[0.05]'
                        : 'border-red-500/10 bg-white/85 hover:bg-white'
                    )}
                    data-testid={`live-match-link-${match.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge className="rounded-full bg-red-500 text-white">LIVE</Badge>
                          <span className={cn('text-xs', subtleTextClass)}>{match.round_name}</span>
                        </div>
                        <p className={cn('mt-3 text-lg font-semibold', headingClass)}>
                          {match.team1?.name} vs {match.team2?.name}
                        </p>
                        {innings && (
                          <p className={cn('mt-2 text-sm', mutedTextClass)}>
                            {innings.batting_team?.name}:{' '}
                            <span className={cn('font-semibold score-display', headingClass)}>
                              {innings.runs}/{innings.wickets}
                            </span>{' '}
                            ({innings.overs}.{innings.balls})
                          </p>
                        )}
                      </div>
                      <Button size="sm" variant={isOrganizer() ? 'destructive' : 'outline'} className="rounded-full">
                        {isOrganizer() ? 'Score' : 'Watch'}
                        <ArrowRight className="ml-1 h-3 w-3" />
                      </Button>
                    </div>
                  </Link>
                );
              })}
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="tournament-menu">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Link
                key={section.path}
                to={section.path}
                className="stagger-in"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`menu-item-${section.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Card
                  className={cn(
                    'h-full rounded-[28px] transition-all hover:-translate-y-0.5',
                    isDark ? 'border-white/10 bg-white/[0.04] hover:bg-white/[0.07]' : 'border-slate-900/10 bg-white/82 hover:bg-white'
                  )}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${section.bg}`}>
                        <Icon className={`h-6 w-6 ${section.tone}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className={cn('font-semibold', headingClass)}>{section.label}</h3>
                        <p className={cn('mt-1 text-sm', subtleTextClass)}>{section.description}</p>
                      </div>
                      <ArrowRight className={cn('h-5 w-5 shrink-0', isDark ? 'text-white/30' : 'text-slate-400')} />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <Card className={cn('rounded-[28px]', surfaceCardClass)} data-testid="upcoming-matches-card">
            <CardHeader className="pb-3">
              <CardTitle className={cn('flex items-center gap-2 text-lg', headingClass)}>
                <Clock3 className="h-5 w-5 text-primary" />
                Upcoming Fixtures
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingMatches.length === 0 ? (
                <div className={cn('rounded-2xl border border-dashed px-4 py-10 text-center', isDark ? 'border-white/10 text-white/45' : 'border-slate-900/10 text-slate-500')}>
                  No upcoming fixtures yet
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingMatches.map((match) => (
                    <div
                      key={match.id}
                      className={cn('flex items-center justify-between gap-3 rounded-2xl border p-4', innerCardClass)}
                      data-testid={`upcoming-match-${match.id}`}
                    >
                      <div className="min-w-0">
                        <p className={cn('font-medium', headingClass)}>{match.team1?.name} vs {match.team2?.name}</p>
                        <p className={cn('mt-1 text-sm', subtleTextClass)}>
                          {new Date(match.match_date).toLocaleDateString()}
                          {match.match_time && ` at ${match.match_time}`}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className={cn(
                          isDark ? 'border-white/10 bg-white/[0.04] text-white/70' : 'border-slate-900/10 bg-white/75 text-slate-700'
                        )}
                      >
                        {match.round_name}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}

              <Link to={`/tournaments/${tournamentId}/matches`}>
                <Button
                  variant="ghost"
                  className={cn('mt-4 w-full gap-2 rounded-2xl', isDark ? 'text-white hover:bg-white/[0.06]' : 'text-slate-900 hover:bg-slate-900/[0.05]')}
                >
                  View Full Match Schedule
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className={cn('rounded-[28px]', surfaceCardClass)}>
            <CardHeader className="pb-3">
              <CardTitle className={cn('flex items-center gap-2 text-lg', headingClass)}>
                <Sparkles className="h-5 w-5 text-primary" />
                Tournament Hub Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className={cn('rounded-2xl border p-4', innerCardClass)}>
                <p className={cn('font-semibold', headingClass)}>Unified Match Flow</p>
                <p className={cn('mt-2 text-sm leading-6', mutedTextClass)}>
                  Live matches route organizers into scoring and viewers into the public live scoreboard without changing the tournament hub.
                </p>
              </div>
              <div className={cn('rounded-2xl border p-4', innerCardClass)}>
                <p className={cn('font-semibold', headingClass)}>Fast Navigation</p>
                <p className={cn('mt-2 text-sm leading-6', mutedTextClass)}>
                  Teams, schedule, standings, stats, and announcements all sit one tap away from the main tournament surface.
                </p>
              </div>
              <div className={cn('rounded-2xl border p-4', innerCardClass)}>
                <p className={cn('font-semibold', headingClass)}>Responsive Intent</p>
                <p className={cn('mt-2 text-sm leading-6', mutedTextClass)}>
                  The hero and live sections stay prominent on mobile, while desktop expands this into a true tournament operations hub.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default TournamentDetail;
